/**
 * P1 roles registry — the ONE shared source for the four controlled role
 * vocabularies (Build J, 2026-07-06).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS FILE EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  Roles in P1 were NEVER free-text — four controlled vocabularies already
 *  live in the code (owner correction + Silas B, verified). They were just
 *  scattered across `types/personModel.ts` (the 3-axis intake vocab) and
 *  `lib/contactModel.ts` (the project-participation vocab), so every surface
 *  reached for a different one. This module LIFTS all four into a single P1
 *  roles source WITHOUT redefining any enum — it re-exports the canonical
 *  values/pickers/label fns and composes them into one registry, plus the
 *  pure derivation the Contacts directory uses to surface a contact across
 *  the role-based lenses (Build K).
 *
 *  ADDITIVE ONLY. No enum is redeclared here; the canonical definitions stay
 *  in personModel.ts / contactModel.ts (SSOT). This file is a composition +
 *  derivation layer, unit-tested in one spot (`test/p1Roles.test.cjs`).
 * ─────────────────────────────────────────────────────────────────────────
 */

import {
  type ParticipantType,
  type RelationToClient,
  type WorkRole,
  type Department,
  PARTICIPANT_TYPE_VALUES,
  RELATION_TO_CLIENT_VALUES,
  RELATION_TO_CLIENT_GROUPS,
  WORK_ROLE_VALUES,
  WORK_ROLE_GROUPS,
  DEPARTMENT_VALUES,
  relationToClientLabel,
  workAxisLabel,
} from '../types/personModel';
import {
  type ParticipantRole,
  participantLabel,
} from './contactModel';

/* ═══════════════════════ 1. Re-export the four vocabularies ═══════════════════════
 *
 * The canonical enums stay where they are defined; consumers import them FROM
 * HERE so there is one P1 roles entry point. (Re-export, never redefine.)
 */
export type {
  ParticipantType,
  RelationToClient,
  WorkRole,
  Department,
  ParticipantRole,
};
export {
  PARTICIPANT_TYPE_VALUES,
  RELATION_TO_CLIENT_VALUES,
  RELATION_TO_CLIENT_GROUPS,
  WORK_ROLE_VALUES,
  WORK_ROLE_GROUPS,
  DEPARTMENT_VALUES,
  relationToClientLabel,
  workAxisLabel,
  participantLabel,
};

/* ═══════════════════════ 2. The unified registry ═══════════════════════
 *
 * One descriptor per vocabulary — id, its axis/purpose, canonical values, the
 * optgroup picker (when it has one), and the human-label fn. A picker/field
 * component can iterate `P1_ROLE_VOCABULARIES` instead of hard-coding a list,
 * so every surface reads the SAME four lists.
 */

/** The stable id of each P1 role vocabulary. */
export type P1RoleVocabularyId =
  | 'participant_type'
  | 'relation_to_client'
  | 'work_role'
  | 'department'
  | 'participant_role';

/** A picker optgroup (label → the values under it). */
export interface P1RoleGroup {
  label: string;
  values: readonly string[];
}

/** A single controlled role vocabulary, described uniformly. */
export interface P1RoleVocabulary {
  id: P1RoleVocabularyId;
  /** Human label for the vocabulary itself (e.g. "Work role"). */
  label: string;
  /** One-line note on which axis / purpose the vocabulary serves. */
  axis: string;
  /** The canonical values, in UI order. */
  values: readonly string[];
  /** Optgroup picker, when the vocabulary defines one (else undefined). */
  groups?: readonly P1RoleGroup[];
  /** PURE — the human label for one value of this vocabulary. */
  labelOf: (value: string) => string;
}

/** Title-case fall-back label (mirrors the personModel label fns' fallback). */
function titleCase(value: string): string {
  return value
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** The four controlled vocabularies, lifted into one registry (owner: never free-text). */
export const P1_ROLE_VOCABULARIES: readonly P1RoleVocabulary[] = [
  {
    id: 'participant_type',
    label: 'Participant type',
    axis: 'Occupancy / visibility / legal (Vastu zones).',
    values: PARTICIPANT_TYPE_VALUES,
    labelOf: titleCase,
  },
  {
    id: 'relation_to_client',
    label: 'Relation to client',
    axis: 'Kinship / business relation to the native (relational astrology + family structure).',
    values: RELATION_TO_CLIENT_VALUES,
    groups: RELATION_TO_CLIENT_GROUPS,
    labelOf: relationToClientLabel,
  },
  {
    id: 'work_role',
    label: 'Work role',
    axis: 'Organisation structure (designation + reporting line).',
    values: WORK_ROLE_VALUES,
    groups: WORK_ROLE_GROUPS,
    labelOf: workAxisLabel,
  },
  {
    id: 'department',
    label: 'Department',
    axis: 'Organisation structure (functional department).',
    values: DEPARTMENT_VALUES,
    labelOf: workAxisLabel,
  },
  {
    id: 'participant_role',
    label: 'Project role',
    axis: 'Project participation (the directory role tabs read this today).',
    values: ['primary_client', 'co_client', 'family', 'consultant', 'partner', 'staff'],
    labelOf: (v) => participantLabel(v as ParticipantRole),
  },
] as const;

/* ═══════════════════════ 3. Role-based-lens derivation (Build K) ═══════════════════════
 *
 * The directory surfaces a contact across LENSES driven by the roles it wears.
 * Historically the derivation read ONLY the project-participation vocab
 * (`ParticipantRole`), so professionals and organisation members were invisible
 * (the two-role-system split — datamap R6). This section reconciles the systems
 * onto ONE derived set that ALSO reflects the 3-axis personModel vocab.
 *
 * DURABLE INPUTS ONLY (Silas Step-0 verdict, 2026-07-06): this derivation reads
 * only what lives at rest AND is already loaded by the directory —
 *   • the project-participation role set (projects[].participants[].role, R4);
 *   • group membership by type (families/{id}.type — family vs organization);
 *   • each membership's `relationship` label (families/{id}.members[].relationship,
 *     written by intake from relation_to_client, or by staff on the group).
 * It does NOT reach into `clients/{cid}/persons/{personId}` (the AD-019 hub that
 * durably holds the precise participant_type / work_role / department). Surfacing
 * those precisely in the directory needs Build C (persist the org-axis onto
 * `families/{id}.members[]`) — see the note at the bottom of this file.
 */

/**
 * The DERIVED role/lens markers a contact can carry in the directory. Extends
 * the project-participation vocab (+ its two legacy fall-backs) with the
 * derived cross-vocabulary lenses.
 */
export type P1LensRole =
  | ParticipantRole
  | 'legacy_client'
  | 'legacy_partner'
  /** Belongs to an `organization`-type group (work_role axis, coarse). */
  | 'org_member'
  /** Belongs to a `family`-type group (relation_to_client axis, coarse). */
  | 'family_member'
  /** An outside professional (external_professional / consultant / vendor / contractor). */
  | 'professional';

/** The base role set the project layer computes (before enrichment). */
export type ProjectRole = ParticipantRole | 'legacy_client' | 'legacy_partner';

/**
 * The external-professional role values across the two work vocabularies. A
 * contact wearing any of these (as a project role OR an org-membership label)
 * belongs in the Professionals lens (external_professional is a ParticipantType;
 * consultant/vendor/contractor are the WorkRole "External" group + the R4
 * consultant project role).
 */
export const EXTERNAL_PROFESSIONAL_ROLE_VALUES: ReadonlySet<string> = new Set([
  'external_professional',
  'consultant',
  'vendor',
  'contractor',
]);

/** A minimal view of one group membership the derivation reads. */
export interface GroupMembershipLite {
  type: 'family' | 'organization';
  /** The legacy free-text relationship label, when set. */
  relationship?: string;
  /* Build C (2026-07-06) — the durable org-axis + kinship now persisted onto
   * `families/{id}.members[]`, so the derivation sharpens (a member captured at
   * intake as work_role:'vendor' now reads as `professional`, not just an
   * `org_member`). All optional; a legacy member without them is unchanged. */
  work_role?: string;
  relation_to_client?: string;
  department?: string;
}

/** PURE — is this membership an outside professional (by work_role OR label)? */
function membershipIsExternalProfessional(g: GroupMembershipLite): boolean {
  return (
    (!!g.work_role && EXTERNAL_PROFESSIONAL_ROLE_VALUES.has(g.work_role)) ||
    (!!g.relationship && EXTERNAL_PROFESSIONAL_ROLE_VALUES.has(g.relationship))
  );
}

/**
 * PURE — reconcile the two role systems into ONE derived lens set for a contact.
 *
 * Starts from the project-participation roles (unchanged, so every legacy path —
 * legacy_client / legacy_partner / the R4 values — behaves exactly as before),
 * then ADDS the derived cross-vocabulary lenses from durable group membership:
 *   • an `organization`-type membership → `org_member` (Organisations lens);
 *   • a `family`-type membership → `family_member` (Family lens);
 *   • a `consultant` project role, OR any membership whose relationship is an
 *     external-professional value → `professional` (Professionals lens).
 *
 * Additive: it never removes a role the project layer found. `groups` defaults
 * to empty, so an old call site passing only project roles is unchanged.
 */
export function enrichContactRoles(
  projectRoles: Iterable<ProjectRole>,
  groups: readonly GroupMembershipLite[] = [],
): Set<P1LensRole> {
  const out = new Set<P1LensRole>(projectRoles);

  for (const g of groups) {
    if (g.type === 'organization') {
      out.add('org_member');
      // An external work_role (or an external free-text label) on an org
      // membership → also a professional. Build C persists work_role onto the
      // member, so a vendor/contractor now sharpens here.
      if (membershipIsExternalProfessional(g)) out.add('professional');
    } else if (g.type === 'family') {
      out.add('family_member');
    }
  }

  // The R4 consultant is, by definition, an outside professional.
  if (out.has('consultant')) out.add('professional');

  return out;
}

/**
 * PURE — the compact human-readable badge list for a derived lens set. Mirrors
 * the directory's existing badge order and copy, extended with the two new
 * lenses (Professional, Organisation). Returns `['Contact']` when a contact
 * wears no recognised role (unchanged fall-back).
 */
export function lensBadges(roles: ReadonlySet<P1LensRole>): string[] {
  const out: string[] = [];
  if (roles.has('primary_client') || roles.has('legacy_client')) out.push('Client');
  if (roles.has('co_client')) out.push('Co-client');
  if (roles.has('partner') || roles.has('legacy_partner')) out.push('Partner');
  if (roles.has('professional')) out.push('Professional');
  if (roles.has('consultant')) out.push('Consultant');
  if (roles.has('family') || roles.has('family_member')) out.push('Family');
  if (roles.has('org_member')) out.push('Organisation');
  if (roles.has('staff')) out.push('AV Staff');
  return out.length > 0 ? out : ['Contact'];
}

/* ─────────────────────────────────────────────────────────────────────────
 *  BUILD-C STATUS (Silas 2026-07-06)
 *
 *  DONE (write path): `families/{id}.members[]` now durably carries work_role /
 *  reports_to / department / relation_to_client, persisted from the intake
 *  persons by `upsertContactsAndGroupFromIntake`. So a member captured at intake
 *  as `work_role: 'vendor'` sharpens to `professional` here (see
 *  `membershipIsExternalProfessional`), and org/family membership drives the
 *  Organisation/Family lenses.
 *
 *  REMAINING (Felix follow-on): the Families GROUP EDITOR captures only a
 *  free-text `relationship` today — there is no structured work_role / reports_to
 *  input on the group side yet. Until that UI lands, the org-axis is populated
 *  only via the intake path (not by editing a group directly). This derivation
 *  needs no further change when that UI arrives — it already reads the axes.
 * ───────────────────────────────────────────────────────────────────────── */

/* ═══════════════════════ 4. Contact-side role assignment (Felix, §6 punch-list) ═══════════════════════
 *
 * Build K reconciled the roles the directory DERIVES from projects + group
 * memberships. But the punch-list also needs a role that staff assign DIRECTLY
 * on a contact — at create ("land a new professional in the Professionals lens,
 * not just All") and from the contact editor. Those directly-assigned roles are
 * stored as controlled values on `clients/{cid}.roleTags[]` (the field the AD-040
 * coordination path already writes for architect / interior_designer) and folded
 * into the SAME lens set here, so a directly-assigned professional/partner/etc.
 * surfaces in the matching lens exactly like a derived one.
 *
 * Still controlled, never free-text: the create/edit picker iterates
 * `ASSIGNABLE_CONTACT_ROLES`; `lensRolesForContactTags` maps whatever is stored
 * (including the legacy `architect` / `interior_designer` tags) onto lens roles.
 * "Client" is deliberately NOT assignable — it stays a DERIVED status from
 * project linkage (spec §3), so it can never be set by hand.
 */

/** One curated, directly-assignable contact role → the lens it lands in. */
export interface AssignableContactRole {
  /** The controlled value stored on `clients/{cid}.roleTags[]`. */
  value: string;
  /** Human label for the create/edit picker. */
  label: string;
  /** The directory lens this role surfaces the contact in. */
  lens: P1LensRole;
}

/**
 * The curated set of roles a staff member can assign directly on a contact
 * (at create or from the editor). Controlled — never free-text. "Client" is
 * absent by design (derived from project linkage, §3).
 */
export const ASSIGNABLE_CONTACT_ROLES: readonly AssignableContactRole[] = [
  { value: 'professional', label: 'Professional (architect / vendor / contractor)', lens: 'professional' },
  { value: 'consultant', label: 'Consultant', lens: 'consultant' },
  { value: 'partner', label: 'Channel partner', lens: 'partner' },
  { value: 'family', label: 'Family member', lens: 'family_member' },
  { value: 'staff', label: 'AV staff', lens: 'staff' },
] as const;

/**
 * PURE — map the values stored on `clients/{cid}.roleTags[]` onto the derived
 * lens roles. Handles the curated `ASSIGNABLE_CONTACT_ROLES` values AND the
 * legacy AD-040 coordination tags (`architect` / `interior_designer`, both
 * professionals). A `consultant` tag is, by definition, also a professional
 * (mirrors `enrichContactRoles`). Unknown tags map to no lens (ignored).
 */
export function lensRolesForContactTags(
  tags: Iterable<string> | null | undefined,
): P1LensRole[] {
  const out = new Set<P1LensRole>();
  for (const raw of tags ?? []) {
    const t = String(raw || '').trim().toLowerCase();
    if (!t) continue;
    if (t === 'partner') out.add('partner');
    else if (t === 'family' || t === 'family_member') out.add('family_member');
    else if (t === 'staff') out.add('staff');
    else if (t === 'consultant') {
      out.add('consultant');
      out.add('professional');
    } else if (
      t === 'professional' ||
      t === 'architect' ||
      t === 'interior_designer' ||
      EXTERNAL_PROFESSIONAL_ROLE_VALUES.has(t)
    ) {
      out.add('professional');
    }
  }
  return [...out];
}
